<template>
  <v-container
    id="profile"
    class="profile py-7"
  >
    <v-alert
      v-if="!hasValidToken"
      type="warning"
    >
      You can't edit this profile or receive recent information about it because your
      authorization token was expired.<br> Log in or connect parent profile to stay updated

      <template v-slot:append>
        <v-btn
          :to="{ name: 'switch-child' }"
        >
          <v-icon left>login</v-icon>
          Login
        </v-btn>
      </template>
    </v-alert>

    <NotLinkedProfileAlert v-if="!activeChild.parent_id" :id="activeChild.id" class="mx-auto" />

    <h5 class="text-h5 mb-3">Player Profile</h5>
    <v-row class="mt-0">
      <v-col cols="12" md="3">
        <v-img
          contain
          :aspect-ratio="1"
          :src="activeChildAvatarSrc"
          :max-height="$vuetify.breakpoint.smAndDown ? 240 : 400"
          width="100%"
          class="grey lighten-3"
        >
          <v-btn
            v-if="allowEditFields && !activeChild.school_id"
            icon
            absolute
            outlined
            right
            color="black"
            class="mt-3"
            :to="{ name: 'newgame', query: { edit: 'avatar' } }"
          >
            <v-icon>edit</v-icon>
          </v-btn>
        </v-img>
      </v-col>

      <v-col cols="12" md="9">
        <v-row>
          <v-col cols="12" md="6">
            <div class="subtitle-1">Username</div>
            <v-text-field
              v-model="activeChild.nickname"
              readonly
              solo
              flat
              background-color="grey lighten-3"
              hide-details
            >
              <template v-if="allowEditFields" v-slot:append>
                <v-btn
                  icon
                  :to="{ name: 'newgame', query: { edit: 'name' } }"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <div class="subtitle-1">Gender</div>
            <v-text-field
              v-model="activeChild.gender"
              readonly
              solo
              flat
              background-color="grey lighten-3"
              hide-details
            >
              <template v-if="allowEditFields && !activeChild.school_id" v-slot:append>
                <v-btn
                  icon
                  :to="{ name: 'newgame', query: { edit: 'gender' } }"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <div class="subtitle-1">Grade</div>
            <v-text-field
              v-model="childGrade"
              readonly
              solo
              flat
              background-color="grey lighten-3"
              hide-details
            >
              <template v-if="allowEditFields && !activeChild.school_id" v-slot:append>
                <v-btn
                  icon
                  :to="{ name: 'newgame', query: { edit: 'grade' } }"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <h5 class="text-h5 mt-12">Account Information</h5>

    <div v-if="!activeChild.parent_id" class="not-linked-account-label py-2">
      <v-icon>warning</v-icon>
      <span>
        This profile not linked to any parent account. Use profile ID to link this profile on the
        <a href="https://growmindgrow.com/parent/account/children">account page.</a>
      </span>
    </div>

    <v-row class="mt-0">
      <v-col cols="12" md="6" lg="4">
        <div class="subtitle-1">Profile ID code</div>
        <v-text-field
          v-model="activeChild.id"
          readonly
          solo
          flat
          background-color="grey lighten-3"
          hide-details
        >
          <template v-slot:append>
            <v-btn
              icon
              @click="$copyText(activeChild.id)"
            >
              <v-icon>file_copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6" lg="4">
        <div class="subtitle-1">Linked parent</div>
        <!-- TODO put real parent email -->
        <v-text-field
          v-model="activeChild.parent_id"
          readonly
          solo
          flat
          background-color="grey lighten-3"
          hide-details
        >
          <template v-if="activeChild.parent_id" v-slot:prepend-inner>
            <v-icon
              color="green"
              class="mr-1"
            >
              verified
            </v-icon>
          </template>
          <template v-slot:append>
            <v-btn
              v-if="activeChild.parent_id"
              icon
              class="mr-2"
              @click="$copyText(activeChild.parent_id)"
            >
              <v-icon>file_copy</v-icon>
            </v-btn>
            <v-btn
              icon
              target="_blank"
              :href="PARENT_SITE_CHILDREN_URL"
            >
              <v-icon>open_in_new</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6" lg="4">
        <div class="subtitle-1">Linked school</div>
        <v-text-field
          :value="activeChild.school_id || activeChild.requested_school_id"
          readonly
          solo
          flat
          background-color="grey lighten-3"
          hide-details
        >
          <template v-if="activeChild.school_id" v-slot:prepend-inner>
            <v-icon
              color="green"
              class="mr-1"
            >
              verified
            </v-icon>
          </template>
          <template v-slot:append>
            <v-btn
              v-if="activeChild.school_id"
              icon
              class="mr-2"
              @click="$copyText(activeChild.school_id)"
            >
              <v-icon>file_copy</v-icon>
            </v-btn>
            <v-btn
              v-if="allowEditFields"
              icon
              @click="openEditDialog(EditChildDialogKey.LinkSchoolDialog)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6" lg="4">
        <div class="subtitle-1">Account Email</div>
        <v-text-field
          v-model="activeChild.email"
          readonly
          solo
          flat
          background-color="grey lighten-3"
          hide-details
        >
          <template v-slot:append>
            <v-btn
              v-if="activeChild.email"
              icon
              class="mr-2"
              @click="$copyText(activeChild.email)"
            >
              <v-icon>file_copy</v-icon>
            </v-btn>
            <v-btn
              v-if="allowEditFields"
              icon
              @click="openEditDialog(EditChildDialogKey.EditChildEmailDialog)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6" lg="4">
        <div class="subtitle-1">Child Name</div>
        <v-text-field
          :value="childFullName"
          readonly
          solo
          flat
          background-color="grey lighten-3"
          hide-details
        >
          <template v-slot:append>
            <v-btn
              v-if="childFullName"
              icon
              class="mr-2"
              @click="$copyText(childFullName)"
            >
              <v-icon>file_copy</v-icon>
            </v-btn>
            <v-btn
              v-if="allowEditFields"
              icon
              @click="openEditDialog(EditChildDialogKey.EditChildNameDialog)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6" lg="4">
        <div class="subtitle-1">Birthday</div>
        <v-text-field
          :value="childBirthday"
          readonly
          solo
          flat
          background-color="grey lighten-3"
          hide-details
        >
          <template v-slot:append>
            <v-btn
              v-if="allowEditFields"
              icon
              @click="openEditDialog(EditChildDialogKey.EditChildBirthdayDialog)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col v-if="childRegistrationDate" cols="12" md="6" lg="4">
        <div class="subtitle-1">Registration Date</div>
        <v-text-field
          :value="childRegistrationDate"
          readonly
          solo
          flat
          background-color="grey lighten-3"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="6" lg="4">
        <div class="subtitle-1">Password</div>
        <v-btn
          :disabled="!allowEditFields || !activeChild.email"
          outlined
          block
          large
          @click="openEditDialog(EditChildDialogKey.EditChildPasswordDialog)"
        >
          Change account password
        </v-btn>
      </v-col>
    </v-row>

    <h5 class="text-h5 mt-12">Achievements</h5>
    <v-row class="mt-0">
      <v-col>
        <!-- TODO implement achievements section -->
        <v-alert
          type="info"
          outlined
          color="warning"
          border="left"
        >
          <span>Section is under development. It'll coming soon</span>
        </v-alert>
      </v-col>
    </v-row>

    <template v-for="editDialogKey in Object.values(EditChildDialogKey)">
      <component
        :is="editDialogKey"
        v-if="dialogInitialized[editDialogKey]"
        :key="editDialogKey"
        v-model="show[editDialogKey]"
      />
    </template>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { AVATARS_FOLDER_URL, PARENT_SITE_CHILDREN_URL } from '@/configs/urls';
import { findJWTByProfileId } from '@/helpers/auth';

const EditChildDialogKey = {
  LinkSchoolDialog: 'LinkSchoolDialog',
  EditChildEmailDialog: 'EditChildEmailDialog',
  EditChildNameDialog: 'EditChildNameDialog',
  EditChildBirthdayDialog: 'EditChildBirthdayDialog',
  EditChildPasswordDialog: 'EditChildPasswordDialog',
};

const editDialogKeysObj = Object
  .values(EditChildDialogKey)
  .reduce((acc, k) => ({ ...acc, [k]: false }), {});

export default {
  name: 'ChildProfile',
  components: {
    NotLinkedProfileAlert: () => import('@/components/NotLinkedProfileAlert.vue'),
    [EditChildDialogKey.LinkSchoolDialog]: () => import('@/components/modals/profile/LinkSchoolDialog.vue'),
    [EditChildDialogKey.EditChildEmailDialog]: () => import('@/components/modals/profile/EditChildEmailDialog.vue'),
    [EditChildDialogKey.EditChildNameDialog]: () => import('@/components/modals/profile/EditChildNameDialog.vue'),
    [EditChildDialogKey.EditChildBirthdayDialog]: () => import('@/components/modals/profile/EditChildBirthdayDialog.vue'),
    [EditChildDialogKey.EditChildPasswordDialog]: () => import('@/components/modals/profile/EditChildPasswordDialog.vue'),
  },
  data() {
    return {
      PARENT_SITE_CHILDREN_URL,
      EditChildDialogKey,
      show: {
        ...editDialogKeysObj,
      },
      dialogInitialized: {
        ...editDialogKeysObj,
      },
    };
  },
  computed: {
    ...mapGetters({
      parentAccount: 'parent/parentAccount',
      childProfile: 'children/activeChildProfile',
    }),
    activeChild() {
      return this.childProfile || {};
    },
    hasValidToken() {
      try {
        return !!findJWTByProfileId(this.$store.getters, this.activeChild.id);
      } catch {
        return false;
      }
    },
    allowEditFields() {
      return this.hasValidToken;
    },
    activeChildAvatar() {
      if (this.activeChild.avatar) {
        return this.activeChild.avatar;
      }
      return this.activeChild.gender === 'boy'
        ? 'DefaultBoyProfile.png'
        : 'DefaultGirlProfile.png';
    },
    activeChildAvatarSrc() {
      return `${AVATARS_FOLDER_URL}${this.activeChildAvatar}`;
    },
    childGrade() {
      const grade = Number(this.activeChild.grade);
      if (isNaN(grade)) return this.activeChild.grade;
      if (grade < 0) return 'Pre-K';
      if (grade === 0) return 'K';
      return grade;
    },
    childFullName() {
      if (!this.activeChild.first_name || !this.activeChild.last_name) return null;
      return `${this.activeChild.first_name} ${this.activeChild.last_name}`;
    },
    childBirthday() {
      if (!this.activeChild.birthday) return;
      return this.$moment(this.activeChild.birthday).format('MM/DD/YYYY');
    },
    childRegistrationDate() {
      if (!this.activeChild.registration_date) return;
      return this.$moment(this.activeChild.registration_date).format('MM/DD/YYYY');
    },
  },
  methods: {
    openEditDialog(dialogKey) {
      this.dialogInitialized[dialogKey] = true;
      this.show[dialogKey] = true;
    },
  },
};
</script>

<style scoped lang="scss">
#profile {
  .not-linked-account-label {
    display: flex;
    align-items: center;

    i {
      color: red !important;
      margin-right: 5px;
      width: 20px;
      height: 20px;
    }

    span {
      color: red;
    }

    a {
      color: red;
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    }
  }
}
</style>
